import { Collapse, Modal, Typography } from '@mui/material';
import mixpanel from '@shared/lib/mixpanel/mixpanel';
import { makeStyles } from '@shared/theme';
import { getCookie, setCookie } from '@shared/util/cookie';
import Button from '@ui/Button';
import Link from '@ui/Link';
import NavButton from '@ui/NavButton';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import CookieForm from './CookieForm';

export const cookieSections = ['essential_cookies', 'analytics_cookies'] as const;

export type CookieSectionType = (typeof cookieSections)[number];

let hasDismissedCookie = false;

const initializeCookies = (initalValue: boolean) =>
  cookieSections.reduce((prev, current) => ({ ...prev, [current]: initalValue }), {});

const initialCookieValue: Record<string, any> = initializeCookies(true);

const useStyles = makeStyles(({ spacing, palette, breakpoints }) => ({
  card: {
    padding: spacing(6),
    backgroundColor: palette.common.white,
    maxWidth: spacing(100),
    position: 'absolute',
    bottom: 0,
    [breakpoints.up('md')]: {
      bottom: spacing(8),
      left: spacing(8),
      maxWidth: spacing(130),
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  content: {
    width: '92%',
    '& p': {
      marginBottom: spacing(3),
      '& button': {
        display: 'inline',
        padding: 0,
        margin: 0,
        width: 'auto',
      },
    },
  },
  actions: {
    display: 'grid',
    gap: spacing(4),
  },
  closeButton: {
    marginRight: spacing(-2),
  },
}));

type CookieBannerProps = {
  showBanner?: boolean;
  onLinkClick: () => void;
};

const CookieBanner = ({ showBanner = true, onLinkClick }: CookieBannerProps) => {
  const { classes } = useStyles();
  const setCookiePreferences = useRef(initialCookieValue);
  const [showCookiesForm, setShowCookiesForm] = useState(false);

  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(hasDismissedCookie);

  const handleCloseForm = useCallback(() => setShowCookiesForm(false), []);
  const handleClose = useCallback(() => {
    setOpen(false);
    hasDismissedCookie = true;
    setDismissed(true);
  }, [setDismissed]);
  const handleSave = useCallback(() => {
    setCookie('cookiePreferences', setCookiePreferences.current);

    if (setCookiePreferences.current.analytics_cookies) {
      mixpanel.enable('cookie');
    }

    handleClose();
  }, [handleClose]);

  const handleChange = useCallback((setting: CookieSectionType) => {
    const preferences = setCookiePreferences.current;
    setCookiePreferences.current = { ...preferences, [setting]: !preferences[setting] };
  }, []);

  const handleRejectAll = useCallback(() => {
    setCookiePreferences.current = { ...initializeCookies(false), essential_cookies: true };
    handleSave();
  }, [handleSave]);

  useEffect(() => {
    const cookie = getCookie('cookiePreferences');
    if (!cookie && showBanner) {
      setOpen(true);
    }
  }, [showBanner]);

  if (dismissed) {
    return null;
  }

  return (
    <Modal
      open={open && showBanner}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className={classes.card}>
        <Collapse in={showCookiesForm} mountOnEnter unmountOnExit>
          <CookieForm onClose={handleCloseForm} onSave={handleSave} onChange={handleChange} />
        </Collapse>
        <Collapse in={!showCookiesForm} mountOnEnter unmountOnExit>
          <div>
            <div className={classes.header}>
              <Typography variant="h4">
                <FormattedMessage id="cookie_banner.we_value_your_privacy" />
              </Typography>
              <NavButton
                className={classes.closeButton}
                which="appbar-close"
                onClick={handleClose}
              />
            </div>
            <div className={classes.content}>
              <Typography variant="body1">
                <FormattedMessage id="cookie_banner.content_1" />
              </Typography>

              <Typography variant="body1">
                <FormattedMessage
                  id="cookie_banner.disclaimer_privacy_policy"
                  values={{
                    link: (chunks: ReactNode[]) => (
                      <Link variant="inherit" onClick={onLinkClick}>
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              </Typography>
            </div>
          </div>
        </Collapse>

        <div className={classes.actions}>
          <Button fullWidth={true} size="medium" onClick={handleSave}>
            <FormattedMessage id="cookie_banner.accept_all" />
          </Button>

          {showCookiesForm ? (
            <Button fullWidth={true} variant="secondary" size="medium" onClick={handleSave}>
              <FormattedMessage id="cookie_banner.save_preferences" />
            </Button>
          ) : (
            <>
              <Button fullWidth={true} variant="secondary" size="medium" onClick={handleRejectAll}>
                <FormattedMessage id="cookie_banner.reject" />
              </Button>

              <Button
                fullWidth={true}
                variant="tertiary"
                size="medium"
                onClick={() => setShowCookiesForm((show) => !show)}
              >
                <FormattedMessage id="cookie_banner.customize_cookies" />
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CookieBanner;
